import { useState } from "react";
import { IndianRupee, Percent, Wallet, Clock, CheckCircle2, Receipt } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { PageHeader, StatCard, LoadingGrid, ErrorState, EmptyState, StatusBadge, Pager } from "@/components/common";
import { inr, fdate } from "@/lib/format";

export default function CreatorEarnings() {
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useApi("/me/earnings", { page });
  if (loading && !data) return <LoadingGrid count={5} className="grid grid-cols-2 lg:grid-cols-5 gap-4" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const s = data.summary;
  return (
    <div>
      <PageHeader eyebrow="Finance" title="Earnings" desc="All values are calculated from your actual brand deals." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 stagger">
        <StatCard label="Total deal value" value={inr(s.total_deal_value)} icon={IndianRupee} testid="earn-total-deal-value" />
        <StatCard label="Commission" value={inr(s.commission)} icon={Percent} testid="earn-commission" />
        <StatCard label="Creator payout" value={inr(s.creator_payout)} icon={Wallet} testid="earn-payout" accent />
        <StatCard label="Pending" value={inr(s.pending)} icon={Clock} testid="earn-pending" />
        <StatCard label="Paid" value={inr(s.paid)} icon={CheckCircle2} testid="earn-paid" />
      </div>
      <p className="eyebrow mt-10 mb-4">Payment history</p>
      {data.payments.length === 0 ? (
        <EmptyState icon={Receipt} title="No payments yet" desc="No earnings yet." testid="payments-empty" />
      ) : (
        <div className="uc-card divide-y divide-border" data-testid="payment-history">
          {data.payments.map((p) => (
            <div key={p.id} className="p-4 sm:px-6 flex items-center justify-between gap-4" data-testid={`payment-row-${p.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{p.brand_name}</p>
                <p className="text-xs text-muted-foreground truncate">{p.campaign_name}{p.reference && ` · Ref ${p.reference}`}{p.payment_date && ` · ${fdate(p.payment_date)}`}</p>
              </div>
              <div className="text-right shrink-0 space-y-1">
                <p className="num">{inr(p.amount)}</p>
                <StatusBadge status={p.status} />
              </div>
            </div>
          ))}
        </div>
      )}
      <Pager page={page} total={data.total} onPage={setPage} />
    </div>
  );
}
